import {
  ensureParentDir,
  writeEnsuringDir,
  type WriteEnsuringDirFlag,
} from "@/utils/fs";
import { in_electron } from "@/utils/in";

import {
  DEFAULT_LEVEL,
  DEFAULT_MAX_ENTRIES,
  DEFAULT_MODULE,
  DEFAULT_OPTIONS,
  defaultFiltersFor,
  IDENTIFIERS,
  IPC_INTERFACE,
} from "./constants";
import { Level, levels } from "./levels";
import {
  buildRendererLogPath,
  deleteSessionAppLogs,
  rendererLogsDir,
  serializeToJsonString,
} from "./utils";

/*
 * Types
 */

export namespace Logger {
  export type Entry = {
    ts: number;
    level: Level;
    module: string;
    message: string;
    args?: string[];
    stack?: string;
  };

  export type Filters = {
    [key: string]: Level;
  };

  export type Filter = {
    module: string;
    level: Level;
  };

  export type Options = {
    defaultMaxSerializedLength: number | null;
    moduleMaxSerializedLength: Record<string, number | null>;
  };
}

const BaseLoggerBrand: unique symbol = Symbol("BaseLogger");
const ModuleLoggerBrand: unique symbol = Symbol("ModuleLogger");

export type BaseLogger = {
  debug: (module: string, ...args: unknown[]) => void;
  info: (module: string, ...args: unknown[]) => void;
  warn: (module: string, ...args: unknown[]) => void;
  error: (module: string, ...args: unknown[]) => void;
  forModule: (module: string) => ModuleLogger;
  readonly [BaseLoggerBrand]: true;
};

export type ModuleLogger = {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  base: () => BaseLogger;
  readonly [ModuleLoggerBrand]: true;
};

/*
 * Globals
 */

let Filters: Logger.Filters = {};
let Options: Logger.Options = cloneOptions(DEFAULT_OPTIONS);
// In-memory log buffer (Web only). Electron renderers persist to disk.
const Buffer: Logger.Entry[] = [];
// Disk persistence (Electron renderer only)
let Identifier: string | null = null;
let Path: string | null = null;
let LogsDir: string | null = null;
let SessionId: string | null = null;
/**
 * Window id in multi-window Electron (e.g. "primary" or "secondary-…"): keys
 * the per-window log file name (see `rendererLogComponent`). Not written on
 * the log lines themselves — a `[win:…]` line prefix would break
 * `textToEntries`, whose patterns are anchored on the leading timestamp;
 * `collect` re-adds attribution inside the message when merging windows.
 */
let WindowId: string | null = null;
let WriteQueue: Promise<void> = Promise.resolve();
/** Avoid flooding the real console when every log line fails to hit disk. */
let diskErrorReported = false;

// Original console methods (captured at install time to avoid recursion).
let rawConsole: {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  log: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  trace?: (...args: unknown[]) => void;
} | null = null;

/*
 * Private functions
 */

function cloneOptions(options: Logger.Options): Logger.Options {
  return {
    defaultMaxSerializedLength: options.defaultMaxSerializedLength,
    moduleMaxSerializedLength: { ...options.moduleMaxSerializedLength },
  };
}

function normalizeOptions(options?: Partial<Logger.Options>): Logger.Options {
  return {
    defaultMaxSerializedLength:
      options?.defaultMaxSerializedLength === undefined
        ? DEFAULT_OPTIONS.defaultMaxSerializedLength
        : options.defaultMaxSerializedLength,
    moduleMaxSerializedLength: {
      ...(options?.moduleMaxSerializedLength ?? {}),
    },
  };
}

function maxSerializedLengthForModule(module: string): number | null {
  if (
    Object.prototype.hasOwnProperty.call(
      Options.moduleMaxSerializedLength,
      module
    )
  ) {
    return Options.moduleMaxSerializedLength[module];
  }
  return Options.defaultMaxSerializedLength;
}

// Convert console arguments to a string array:
//
//   console.log("Hello", { foo: "bar" });
//
// becomes:
//
//   ["Hello", {"foo":"bar"}]
function formatConsoleArgs(module: string, args: unknown[]): string[] {
  const maxLen = maxSerializedLengthForModule(module);
  return args.map((a) => {
    try {
      if (typeof a === "string") return a;
      if (typeof a === "number" || typeof a === "boolean" || a == null)
        return String(a);
      return serializeToJsonString(a, { maxLen });
    } catch {
      try {
        return String(a);
      } catch {
        return "[Unserializable]";
      }
    }
  });
}

function createBaseWrapper(): BaseLogger {
  return {
    [BaseLoggerBrand]: true,
    debug: (module: string, ...args: unknown[]) =>
      logger.renderer.log(module, "debug", ...args),
    info: (module: string, ...args: unknown[]) =>
      logger.renderer.log(module, "info", ...args),
    warn: (module: string, ...args: unknown[]) =>
      logger.renderer.log(module, "warn", ...args),
    error: (module: string, ...args: unknown[]) =>
      logger.renderer.log(module, "error", ...args),
    forModule: createModuleWrapper,
  };
}

function createModuleWrapper(module: string): ModuleLogger {
  return {
    [ModuleLoggerBrand]: true,
    debug: (...args: unknown[]) =>
      logger.renderer.log(module, "debug", ...args),
    info: (...args: unknown[]) => logger.renderer.log(module, "info", ...args),
    warn: (...args: unknown[]) => logger.renderer.log(module, "warn", ...args),
    error: (...args: unknown[]) =>
      logger.renderer.log(module, "error", ...args),
    base: createBaseWrapper,
  };
}

function isFilteredOut(module: string, level: Level): boolean {
  // Warnings/errors are always shown and always captured, regardless of filters.
  if (level === "warn" || level === "error") {
    return false;
  }
  // Modules without an explicit filter fall back to the default level, so info
  // is kept by default. A per-module filter can still raise verbosity (debug)
  // or lower it (warn) for a specific module.
  const min = module in Filters ? Filters[module] : DEFAULT_LEVEL;
  // Filter out entries below the configured minimum level.
  return !levels.gte(level, min);
}

function reportDiskError(error: unknown) {
  if (diskErrorReported) return;
  diskErrorReported = true;
  try {
    // The wrapped console would recurse into capture(); use the original.
    rawConsole?.error("[logger] Failed to persist logs to disk", error);
  } catch {
    // nowhere else to put it
  }
}

/**
 * Queue a disk write. Truncating (`w`) mkdir's the parent first; appends
 * (`a`) only mkdir on ENOENT so a vanished directory (e.g. OS purge of
 * Saved Application State) is recreated without a mkdir on every line.
 */
function enqueueLogFileWrite(
  data: string,
  flag: WriteEnsuringDirFlag
): Promise<void> {
  WriteQueue = WriteQueue.then(async () => {
    const filePath = Path;
    if (!filePath) return;
    try {
      if (flag === "w") {
        await ensureParentDir(filePath);
      }
      await writeEnsuringDir(filePath, data, flag);
      diskErrorReported = false;
    } catch (error) {
      reportDiskError(error);
    }
  });
  return WriteQueue;
}

// Captures console-based (console.log() etc.) log entries.
function capture(
  module: string,
  level: Level,
  args: unknown[],
  stack?: string
): boolean {
  // Apply filtering as early as possible: if filtered out, do not format, do not buffer,
  // and callers can avoid forwarding to the real console too.
  if (isFilteredOut(module, level)) {
    return false;
  }
  const formatted = formatConsoleArgs(module, args);
  const message = formatted[0] ?? "";
  const entry: Logger.Entry = {
    ts: Date.now(),
    level,
    module,
    message,
    args: formatted.length > 1 ? formatted : undefined,
    stack,
  };

  // Keep in-memory logs in Web builds.
  if (!in_electron) {
    Buffer.push(entry);
    if (Buffer.length > DEFAULT_MAX_ENTRIES) {
      Buffer.splice(0, Buffer.length - DEFAULT_MAX_ENTRIES);
    }
  }
  // Persist immediately to disk in Electron renderer (non-blocking).
  if (in_electron && Path) {
    // Keep ordering with a promise chain; never await in the hot path.
    enqueueLogFileWrite(convertEntryToText(entry), "a");
  }

  return true;
}

// Convert timestamp (milliseconds) to "YYYY-MM-DD HH:MM:SS" format
function formatDate(ts: number): string {
  const date = new Date(ts);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

// Convert a log entry to text format: DATE [MODULE][LEVEL] MESSAGE
function convertEntryToText(log: Logger.Entry): string {
  try {
    const date = formatDate(log.ts);
    const module = log.module || DEFAULT_MODULE;

    // Try to parse error objects from the message
    let text = log.message;
    let errorInfo: {
      name?: string;
      message?: string;
      stack?: string;
    } | null = null;

    // Normalize newlines in the message (replace with spaces, collapse multiple spaces)
    if (text) {
      text = text.replace(/\n/g, " ").replace(/\s+/g, " ").trim();
    }

    // Check if message is a stringified error object
    if (
      text &&
      text.startsWith("{") &&
      text.includes('"name"') &&
      text.includes('"message"')
    ) {
      try {
        errorInfo = JSON.parse(text);
        if (errorInfo && errorInfo.name && errorInfo.message) {
          text = `${errorInfo.name}: ${errorInfo.message}`;
        }
      } catch {
        // Not valid JSON, use as-is
      }
    }

    // Handle additional arguments (skip if they duplicate the message)
    if (log.args && log.args.length > 0) {
      const messageLower = (log.message || "").toLowerCase().trim();
      const argsText = log.args
        .filter((arg) => {
          // Skip args that are duplicates of the message
          if (typeof arg === "string") {
            const argLower = arg.toLowerCase().trim();
            // Skip if it's the same as message (case-insensitive)
            if (argLower === messageLower) return false;
            // Skip if message is contained in arg or vice versa (to catch partial duplicates)
            if (
              messageLower &&
              (argLower.includes(messageLower) ||
                messageLower.includes(argLower))
            ) {
              // But allow it if arg contains additional info (like "rpc: {...}")
              if (argLower.length > messageLower.length * 1.5) return true;
              return false;
            }
            return true;
          }
          return true;
        })
        .map((arg) => {
          if (typeof arg === "string") {
            // Remove leading/trailing newlines and normalize internal newlines to spaces
            return arg.trim().replace(/\s+/g, " ");
          }
          const converted = serializeToJsonString(arg);
          // Also normalize newlines in converted JSON
          return converted.replace(/\n/g, " ").replace(/\s+/g, " ");
        })
        .join(" ");

      if (argsText) {
        text = text ? `${text} ${argsText}` : argsText;
      }
    }

    // Use stack from errorInfo if available, otherwise use log.stack
    // If we parsed an error from the message, prefer its stack; otherwise use log.stack
    const stack = errorInfo ? errorInfo.stack || log.stack : log.stack;
    if (stack) {
      // Format stack trace with indentation (all lines indented)
      const stackLines = stack.split("\n").map((line) => `    ${line}`);
      text = text ? `${text}\n${stackLines.join("\n")}` : stackLines.join("\n");
    }

    return `${date} [${module}][${log.level}] ${text}\n`;
  } catch (error) {
    return `[logger] Error converting log entry: ${error instanceof Error ? error.message : String(error)}\n`;
  }
}

// Convert logs to text format: DATE [MODULE][LEVEL] MESSAGE
function convertLogToText(logs: Logger.Entry[]): string {
  try {
    if (!logs || !Array.isArray(logs)) {
      return "[logger] Error: invalid logs input\n";
    }
    const lines: string[] = [];
    for (const log of logs) {
      lines.push(convertEntryToText(log));
    }
    return lines.join("");
  } catch (error) {
    return `[logger] Error converting logs: ${error instanceof Error ? error.message : String(error)}\n`;
  }
}

/*
 * Public functions
 */

export const logger = {
  renderer: {
    /**
     * Initialize the renderer logger with console capture and optional disk persistence.
     *
     * This should be called once per renderer process (app, dashboard, menubar etc.) to set up
     * console interception and log buffering.
     *
     * NOTE: only meaningful in a windowed/renderer context (i.e. not in the main Electron process).
     */
    initialize: async function (
      identifier: string,
      filters: Logger.Filters,
      options: Partial<Logger.Options> = DEFAULT_OPTIONS
    ): Promise<void> {
      // Only meaningful in a windowed/renderer context. In Electron main process,
      // `process.type === "browser"` and `window` is undefined.
      if (
        typeof process !== "undefined" &&
        "type" in process &&
        process.type === "browser"
      )
        return;
      if (typeof window === "undefined") return;

      Identifier = identifier;
      Filters = filters;
      Options = normalizeOptions(options);

      // Install console capture only once.
      if (!rawConsole) {
        rawConsole = {
          debug: window.console.debug.bind(window.console),
          info: window.console.info.bind(window.console),
          log: window.console.log.bind(window.console),
          warn: window.console.warn.bind(window.console),
          error: window.console.error.bind(window.console),
          trace:
            typeof window.console.trace === "function"
              ? window.console.trace.bind(window.console)
              : undefined,
        };

        const wrap = (level: Level, original: (...a: unknown[]) => void) => {
          return (...args: unknown[]) => {
            let stack: string | undefined;
            if (level === "error" || level === "warn") {
              stack = new Error().stack;
            }
            // Use empty module for default console.*() calls
            capture(DEFAULT_MODULE, level, args, stack);
            original(...args);
          };
        };

        window.console.debug = wrap("debug", rawConsole.debug);
        window.console.info = wrap("info", rawConsole.info);
        window.console.log = wrap("info", rawConsole.log);
        window.console.warn = wrap("warn", rawConsole.warn);
        window.console.error = wrap("error", rawConsole.error);
        if (rawConsole.trace) {
          window.console.trace = wrap("debug", rawConsole.trace);
        }
      }

      // Set up disk persistence for this renderer process (Electron only).
      if (in_electron) {
        const { ipcRenderer: ipc } = require("electron");
        try {
          SessionId = await ipc.invoke(IPC_INTERFACE.SESSION);
          try {
            WindowId = await ipc.invoke("get-window-id");
          } catch {
            // Known risk: a null window id falls back to the *primary's* file
            // name (`rendererLogFileNameFor`), so a secondary window whose
            // invoke failed would truncate the primary's log on init. The
            // handler is registered before any window exists, so this should
            // only ever hit for non-main renderers (which don't use the "app"
            // identifier and are unaffected).
            WindowId = null;
          }
        } catch (error) {
          window.console.error(
            "Failed to get log session ID from main process",
            error
          );
          return;
        }

        if (!SessionId) return;

        let userDataPath: unknown;
        try {
          userDataPath = await ipc.invoke(IPC_INTERFACE.USER_DATA);
        } catch (error) {
          window.console.error(
            "Failed to get userData path from main process",
            error
          );
          return;
        }
        if (typeof userDataPath !== "string" || userDataPath.length === 0) {
          window.console.error(
            "Failed to build the log path for the renderer:",
            identifier
          );
          return;
        }

        LogsDir = rendererLogsDir(userDataPath);
        // Each main window writes its own file: the truncating init below
        // would otherwise wipe the shared session log on every window open.
        Path = buildRendererLogPath(
          userDataPath,
          identifier,
          SessionId,
          WindowId
        );

        // Create the directory first, then a fresh file for this session.
        await enqueueLogFileWrite("", "w");
      }
    },

    // Filter management subcomponent.
    filter: {
      // Get the current filters.
      get: function (): Logger.Filters {
        return Filters;
      },

      // Set a filter for a module.
      set: function (module: string, level: Level) {
        Filters[module] = level;
      },

      // Reset all filters to defaults (a fresh copy: `set`/`unset` mutate
      // `Filters` in place, and must not corrupt the shared constants).
      reset: function () {
        Filters = defaultFiltersFor(Identifier);
      },

      // Unset (remove) a module from the filters.
      unset: function (module: string) {
        delete Filters[module];
      },
    },

    // Serialization/truncation options. These are intentionally separate from
    // level filters so investigation sessions can keep the same modules enabled
    // while changing how much payload detail is preserved.
    options: {
      get: function (): Logger.Options {
        return cloneOptions(Options);
      },

      set: function (options: Partial<Logger.Options>) {
        Options = normalizeOptions({ ...Options, ...options });
      },

      setModuleMaxSerializedLength: function (
        module: string,
        maxSerializedLength: number | null
      ) {
        Options = {
          ...Options,
          moduleMaxSerializedLength: {
            ...Options.moduleMaxSerializedLength,
            [module]: maxSerializedLength,
          },
        };
      },

      unsetModuleMaxSerializedLength: function (module: string) {
        const moduleMaxSerializedLength = {
          ...Options.moduleMaxSerializedLength,
        };
        delete moduleMaxSerializedLength[module];
        Options = { ...Options, moduleMaxSerializedLength };
      },

      reset: function () {
        Options = cloneOptions(DEFAULT_OPTIONS);
      },
    },

    // Convert the in-memory logs to text (no-op in Electron).
    toText: function (): string | null {
      if (in_electron) return null;
      if (!Buffer || Buffer.length === 0) return null;
      return convertLogToText(Buffer);
    },

    // Expose session/path for higher-level collectors.
    sessionId: function (): string | null {
      return SessionId;
    },
    path: function (): string | null {
      return Path;
    },
    logsDir: function (): string | null {
      return LogsDir;
    },
    identifier: function (): string | null {
      return Identifier;
    },

    // Clear all logs from the buffer.
    clear: function () {
      if (!in_electron) {
        Buffer.splice(0, Buffer.length);
        return;
      }
      if (!Path) return;
      // The app log is presented merged across the session's windows, so
      // Clear removes every window's file, not just this renderer's. Queue it
      // behind this renderer's pending writes; sibling windows recreate their
      // file on their next append.
      if (Identifier === IDENTIFIERS.APP && LogsDir && SessionId) {
        const logsDir = LogsDir;
        const sessionId = SessionId;
        WriteQueue = WriteQueue.then(() =>
          deleteSessionAppLogs(logsDir, sessionId)
        );
      }
      enqueueLogFileWrite("", "w");
    },

    // Log a message.
    //
    // Note that unlike the native Javascript console, this logger allows
    // for grouping messages by module, allowing to easily filter logs later.
    log: function (module: string, level: Level, ...args: unknown[]) {
      const mod = module || DEFAULT_MODULE;
      const shouldDisplay = capture(mod, level, args);

      // Mirror to console using the original console methods so we don't recurse.
      if (rawConsole && shouldDisplay) {
        let fn: (...args: unknown[]) => void;
        switch (level) {
          case "error":
            fn = rawConsole.error;
            break;
          case "warn":
            fn = rawConsole.warn;
            break;
          case "debug":
            fn = rawConsole.debug;
            break;
          case "info":
          default:
            fn = rawConsole.info;
            break;
        }
        fn(`[${mod}]`, ...args);
      }
    },

    /**
     * Create a logger wrapper with a simpler API (logger.log(module, ...), logger.warn(module, ...), etc.)
     */
    wrapper: function (): BaseLogger {
      return createBaseWrapper();
    },

    /**
     * Create a logger wrapper with a pre-configured module name.
     * Usage: const logger = renderer.wrapperForModule("myModule"); logger.info("message");
     */
    wrapperForModule: function (module: string): ModuleLogger {
      return createModuleWrapper(module);
    },
  },
};
